/**
 * Meeting reminders (T6, D7): one OS notification per upcoming event
 * occurrence this person has accepted or organises, fired at the event's
 * own alert time -- or, when it has none, 5 minutes before start
 * (`.claude/campaign/2026-09-25-scout-2-7.md`'s D7 user decision, "an OS
 * notification from the event's own alerts, or 5 minutes before when an
 * event has none, shown while Agora is open").
 *
 * Uses the Web Notification API, which Electron's renderer implements the
 * same way a browser tab does, so this one module covers both hosts with
 * no bridge call. Permission is asked from a user gesture only
 * (`requestReminderPermission`, called by `shell/AgoraShell.tsx`'s own
 * "Turn on notifications" menu item) -- never on load -- and a denial
 * just means `showReminderNotification` stays a no-op forever; nothing
 * here re-asks.
 *
 * `App.tsx`'s `AgoraView` owns the calendar client already (`counts.ts`'s
 * own `watchCalendarNeedsAction` sits right beside this); `watchReminders`
 * mirrors that module's shape -- an immediate read once identity resolves,
 * then a refetch on every push that says an event or an alert changed --
 * but schedules local timers instead of reporting a count, so it stays
 * framework-free and unit-testable with fake timers (`reminders.test.ts`).
 */
import type { CalendarAlert, CalendarClient, CalendarEvent } from '@wtfalch/calendar/calendar';

import { durationMinutes } from './calendar/layout.ts';
import { type Identity, isOrganiser, myParticipant } from './calendar/participants.ts';
import { pathForCalendar } from './routes.ts';
import { dateParam, todayWindowRange } from './today/rows.ts';

/** D7's own fallback: 5 minutes before start, for an event with no alert
 *  of its own. */
const DEFAULT_OFFSET_MS = -5 * 60_000;

/** `setTimeout` truncates its delay to a 32-bit signed int (a platform
 *  limit, not this module's own choice); a delay past this fires almost
 *  immediately instead of waiting. `scheduleAt` chains shorter waits past
 *  it so an event weeks out (`todayWindowRange`'s own 30-day default
 *  window) still fires at the right moment rather than the instant it is
 *  scheduled. */
const MAX_TIMEOUT_MS = 2_147_483_647;

/** JSCalendar's signed ISO 8601 duration (RFC 8984 §5.6), e.g. `-PT15M` or
 *  `-P1D`. Unlike `calendar/layout.ts`'s own `durationMinutes`, an alert's
 *  offset can be negative and can carry days, so this is its own parser
 *  rather than a reuse -- that one is deliberately narrow to the two units
 *  `CalendarEventDraft.duration` actually uses. Returns milliseconds,
 *  `null` for anything this cannot read (an empty duration, or text that
 *  is not one at all). */
export function parseAlertOffset(iso: string): number | null {
  const match = /^([+-])?P(?:(\d+)W)?(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?)?$/.exec(iso);
  if (!match) return null;
  const [, sign, weeks, days, hours, minutes, seconds] = match;
  if (!weeks && !days && !hours && !minutes && !seconds) return null;
  const ms =
    ((((Number(weeks ?? 0) * 7 + Number(days ?? 0)) * 24 + Number(hours ?? 0)) * 60 +
      Number(minutes ?? 0)) *
      60 +
      Number(seconds ?? 0)) *
    1000;
  return sign === '-' ? -ms : ms;
}

/** When one alert actually fires: its own absolute `when`, or `offset`
 *  applied to the event's start or end (`relativeTo`, defaulting to
 *  `'start'` -- the SDK's own doc comment: a live `CalendarEvent/get`
 *  never sends the default back when the create never sent it either).
 *  `null` when neither half of the alert can be computed at all -- the
 *  SDK's own doc comment names this a real, if unusual, possibility. */
export function alertTriggerTime(
  event: Pick<CalendarEvent, 'start' | 'duration'>,
  alert: CalendarAlert,
): Date | null {
  if (alert.when) {
    const when = new Date(alert.when);
    return Number.isNaN(when.getTime()) ? null : when;
  }
  if (alert.offset === undefined) return null;
  const offsetMs = parseAlertOffset(alert.offset);
  if (offsetMs === null) return null;
  const start = new Date(event.start);
  const anchor =
    alert.relativeTo === 'end'
      ? new Date(start.getTime() + durationMinutes(event.duration) * 60_000)
      : start;
  return new Date(anchor.getTime() + offsetMs);
}

/** The one moment this occurrence's one reminder fires (D7: schedule one
 *  reminder per event occurrence). A `'display'` alert closest to start --
 *  the last call before the meeting, when an imported event carries more
 *  than one (this app's own editor writes none yet: `calendar/Event.tsx`
 *  has no alert field at all, so every alert seen here came from another
 *  client) -- or 5 minutes before start when the event has none of its
 *  own. An `'email'` alert is round-tripped by the SDK but never used to
 *  fire a notification here (the SDK's own doc comment: "only `'display'`
 *  is built"). */
export function reminderTime(event: Pick<CalendarEvent, 'start' | 'duration' | 'alerts'>): Date {
  const start = new Date(event.start);
  const triggers = (event.alerts ?? [])
    .filter((a) => a.action === 'display')
    .map((a) => alertTriggerTime(event, a))
    .filter((d): d is Date => d !== null && !Number.isNaN(d.getTime()));
  if (triggers.length === 0) return new Date(start.getTime() + DEFAULT_OFFSET_MS);
  return triggers.reduce((closest, d) => (d.getTime() > closest.getTime() ? d : closest));
}

/** Stable across a reload: the series' own `uid` plus, for one occurrence
 *  of a recurring event, its `recurrenceId` -- never the expanded
 *  occurrence's own `id`, which `@wtfalch/calendar`'s own doc comment
 *  warns is "positional, not stable" and can be reused by a different
 *  occurrence after a nearby write. */
export function occurrenceKey(event: Pick<CalendarEvent, 'uid' | 'recurrenceId'>): string {
  return `${event.uid}:${event.recurrenceId ?? ''}`;
}

/** Whether `identity` should be reminded of `event` at all -- D7's own
 *  scope: "accepted or organises, not declined". An event with no
 *  `participants` at all (a personal entry nobody invited this account
 *  to) is reminded regardless -- there is no invite to have declined.
 *  Otherwise the person needs a participant row of their own, and it must
 *  be the organiser's or `'accepted'`; `identity` still resolving reads as
 *  "no match yet" here, the same way `counts.ts`'s own
 *  `calendarNeedsActionCount` treats it, so nothing is reminded early off
 *  a guess. A cancelled event and an all-day event are never reminded:
 *  one is not happening, and the other has no start worth a ping. */
export function isReminderable(
  event: Pick<CalendarEvent, 'participants'> & Partial<Pick<CalendarEvent, 'status' | 'isAllDay'>>,
  identity: Identity | null,
): boolean {
  if (event.status === 'cancelled' || event.isAllDay) return false;
  if (!event.participants || event.participants.length === 0) return true;
  const mine = myParticipant(event, identity);
  if (!mine) return false;
  return isOrganiser(mine) || mine.participationStatus === 'accepted';
}

/** Scoped per agora, the same way `agoras.ts`'s own `chatDeviceId` scopes
 *  its own `localStorage` key -- two agoras can each have a real calendar
 *  event whose series reuses the same `uid` (`occurrenceKey` has no agora
 *  in it), and a single shared key would let marking one agora's occurrence
 *  fired silently swallow the other agora's real reminder for it. */
function firedStorageKey(agoraId: string): string {
  return `wtfalch-agora:${agoraId}:reminders-fired`;
}
/** How long a fired occurrence's record is kept -- long enough to survive
 *  any reload within the reminder's own window, short enough that the
 *  record never grows without bound ("never fire twice ... including
 *  after a reload within the window"). */
const FIRED_RETENTION_MS = 24 * 60 * 60 * 1000;

/** Every occurrence fired within `FIRED_RETENTION_MS` for `agoraId`,
 *  pruning anything older on the way out. Wrapped in `try`/`catch`: a
 *  person with storage blocked, full or absent (a test environment) just
 *  loses de-dup across a reload, never the reminder itself. */
function loadFired(agoraId: string, now: Date): Record<string, number> {
  try {
    const raw = window.localStorage.getItem(firedStorageKey(agoraId));
    const record: Record<string, number> = raw ? JSON.parse(raw) : {};
    const cutoff = now.getTime() - FIRED_RETENTION_MS;
    const pruned: Record<string, number> = {};
    for (const [key, firedAt] of Object.entries(record)) {
      if (typeof firedAt === 'number' && firedAt >= cutoff) pruned[key] = firedAt;
    }
    return pruned;
  } catch {
    return {};
  }
}

function hasFired(agoraId: string, key: string, now: Date): boolean {
  try {
    return key in loadFired(agoraId, now);
  } catch {
    return false;
  }
}

function markFired(agoraId: string, key: string, now: Date): void {
  try {
    const record = loadFired(agoraId, now);
    record[key] = now.getTime();
    window.localStorage.setItem(firedStorageKey(agoraId), JSON.stringify(record));
  } catch {
    // Best effort, same rule as every other host call in this app
    // (`counts.ts`'s own `reportBadgeCount`): never blocks the reminder.
  }
}

/** `setTimeout` chained past its own 32-bit delay limit (`MAX_TIMEOUT_MS`),
 *  so a reminder weeks out still fires at the right moment instead of the
 *  instant it is scheduled. `now` is injectable so a fake-timer test's own
 *  clock is what each tick checks against, not the real one. */
function scheduleAt(when: Date, now: () => Date, fire: () => void): () => void {
  let cancelled = false;
  let handle: ReturnType<typeof setTimeout> | null = null;
  const tick = () => {
    if (cancelled) return;
    const delay = when.getTime() - now().getTime();
    if (delay <= 0) {
      fire();
      return;
    }
    handle = setTimeout(tick, Math.min(delay, MAX_TIMEOUT_MS));
  };
  tick();
  return () => {
    cancelled = true;
    if (handle !== null) clearTimeout(handle);
  };
}

/** The fields `showReminderNotification` and `occurrenceKey` need off a due
 *  event -- everything `watchReminders`' `onDue` callback is handed. */
export type ReminderDue = Pick<
  CalendarEvent,
  'id' | 'uid' | 'recurrenceId' | 'title' | 'start' | 'duration' | 'location'
>;

/** Watches upcoming events live, scheduling one local timer per
 *  reminderable occurrence and calling `onDue` exactly once each, at its
 *  own `reminderTime` -- never before, never twice, even across a reload
 *  inside `FIRED_RETENTION_MS` (a small localStorage record). Mirrors
 *  `counts.ts`'s `watchCalendarNeedsAction`: an immediate read once
 *  identity resolves, then a refetch on every push that says
 *  `CalendarEvent` or the dedicated `CalendarAlert` channel changed -- a
 *  changed alert reschedules that event's own timer with no separate
 *  fetch of its own needed. A fetch failure leaves whatever was already
 *  scheduled in place, the same "stay quiet" rule `watchCalendarNeedsAction`
 *  and `watchMailUnread` both already use. `agoraId` scopes the fired-
 *  occurrence record in `localStorage` so a `uid` two agoras happen to
 *  share never lets one agora's fired reminder swallow the other's. */
export function watchReminders(
  client: CalendarClient,
  agoraId: string,
  onDue: (event: ReminderDue) => void,
  options?: { now?: () => Date; daysAhead?: number },
): () => void {
  let live = true;
  let identity: Identity | null = null;
  const now = options?.now ?? (() => new Date());
  let stopTimers: (() => void)[] = [];

  const reschedule = (events: readonly CalendarEvent[]) => {
    for (const stop of stopTimers) stop();
    stopTimers = [];
    for (const event of events) {
      if (!isReminderable(event, identity)) continue;
      const key = occurrenceKey(event);
      const nowValue = now();
      if (hasFired(agoraId, key, nowValue)) continue; // cheap early-out; skip scheduling at all
      const at = reminderTime(event);
      // Opened after the reminder's moment but before the meeting starts:
      // `scheduleAt` fires at once. Once the meeting has started, never.
      if (new Date(event.start).getTime() <= nowValue.getTime()) continue;
      stopTimers.push(
        scheduleAt(at, now, () => {
          // Checked and marked again right here, not only above: two tabs
          // on the same origin (sharing this same localStorage) can each
          // pass the check above and schedule their own timer before
          // either has fired, and a reload can leave a dying page's timer
          // racing the new page's own. This is the guard that actually
          // stops either of those from showing the notification twice --
          // whichever timer's callback runs first wins, the other reads
          // its own `markFired` back and stays quiet.
          const fireNow = now();
          if (hasFired(agoraId, key, fireNow)) return;
          markFired(agoraId, key, fireNow);
          onDue(event);
        }),
      );
    }
  };

  const load = () => {
    client
      .listEvents(todayWindowRange(now(), options?.daysAhead))
      .then((events) => {
        if (live) reschedule(events);
      })
      .catch(() => {
        // A fetch failure just leaves the previous schedule in place.
      });
  };

  client
    .identity()
    .then((id) => {
      identity = id;
      if (live) load();
    })
    .catch(() => {
      // Who is signed in never resolved; nothing to remind without it.
    });

  const sub = client.subscribe((changed) => {
    if (changed.CalendarEvent || changed.CalendarAlert) load();
  });

  return () => {
    live = false;
    sub.close();
    for (const stop of stopTimers) stop();
    stopTimers = [];
  };
}

/** `Notification.permission`, plus the one state a runtime with no
 *  `Notification` constructor at all reads as (a test environment, or a
 *  genuinely old browser). Read fresh on every call rather than cached, so
 *  `shell/AgoraShell.tsx`'s own menu item reflects a grant or a denial made
 *  in this same click. */
export type ReminderPermission = NotificationPermission | 'unsupported';

export function reminderPermission(): ReminderPermission {
  return typeof Notification === 'undefined' ? 'unsupported' : Notification.permission;
}

/** Asks for permission -- only ever called from a user gesture
 *  (`shell/AgoraShell.tsx`'s own "Turn on notifications" menu item is
 *  the one caller in this app), never on load. A denial is terminal: the
 *  menu item stops offering itself the moment `reminderPermission()` reads
 *  anything but `'default'`, so nothing here re-asks. */
export function requestReminderPermission(): Promise<ReminderPermission> {
  if (typeof Notification === 'undefined') return Promise.resolve('unsupported');
  return Notification.requestPermission();
}

/** Fires the one OS notification for a due event, and wires its click to
 *  focus Agora and open the event -- the Web Notification API only, so
 *  this is identical in a browser tab and in Electron's renderer (Electron
 *  implements the same constructor there). A no-op without permission:
 *  `watchReminders` has already marked the occurrence fired by the time
 *  this is called, so a denial matches D7's "if denied, do nothing" rather
 *  than queuing anything to show once permission is later granted. */
export function showReminderNotification(
  event: ReminderDue,
  options: { agoraId: string; go: (path: string) => void },
): void {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
  const start = new Date(event.start);
  const body = [
    start.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }),
    event.location,
  ]
    .filter(Boolean)
    .join(' · ');
  const notification = new Notification(event.title || 'Meeting reminder', {
    body,
    tag: `wtfalch-agora-reminder:${occurrenceKey(event)}`,
  });
  notification.onclick = () => {
    window.focus();
    options.go(pathForCalendar(options.agoraId, { date: dateParam(start), eventId: event.id }));
    notification.close();
  };
}
